import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { DetailCard } from '@tecuity/granite-ui/DetailCard'
import { DetailList } from '@tecuity/granite-ui/DetailList'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { GraniteDataGrid } from '@tecuity/granite-ui/GraniteDataGrid'
import LeftArrowIcon from '@tecuity/sos-icons/react/left-arrow'
import { format } from 'date-fns'
import NotesPanel from '../NotesPanel'
import FileDownloader from 'components/ImageViewer/FileDownloader'
import {
  addDataRequestNote,
  editDataRequestNote,
  deleteDataRequestNote,
  getDataRequestRecordData,
  getEntityNotes,
} from '../RecordDetailsActions'
import { GET_REPORT_BY_REPORT_OBJECT, GET_DATA_REQUEST_DETAIL_SETTINGS } from '../../../requests'
import { setErrorToast } from '../../../actions'

const DataRequestRecordDetails = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const { recordDetails } = useSelector((state) => state.record, shallowEqual)
  const downloader = useRef()
  const [recordDetailSettings, setDetailsSettings] = useState({})
  const [downloadURL, setDownloadURL] = useState(null)
  const [downloadOnNextTick, setDownloadOnNextTick] = useState(false)
  const [dataRequestFilename, setDataRequestFilename] = useState('data-request.zip')
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)

  const dataReqFilingId = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location.pathname])

  useEffect(() => {
    GET_DATA_REQUEST_DETAIL_SETTINGS(
      (recordDetailSettings) => {
        setDetailsSettings(recordDetailSettings)
      },
      (e) => {
        console.error(e)
      }
    )
  }, [])

  useEffect(() => {
    dispatch(getDataRequestRecordData(dataReqFilingId))
    dispatch(getEntityNotes(869, dataReqFilingId))
  }, [dataReqFilingId])

  const saveNewNote = (note) => {
    dispatch(addDataRequestNote(note, dataReqFilingId))
  }

  const editNote = (note) => {
    dispatch(editDataRequestNote(note, dataReqFilingId))
  }

  const deleteNote = (noteId) => {
    dispatch(deleteDataRequestNote(noteId, dataReqFilingId))
  }

  const dataRequestColumns = useMemo(() => {
    if (recordDetailSettings.FieldSettings && recordDetailSettings.FieldSettings['Downloads']) {
      const downloadsFields = recordDetailSettings.FieldSettings['Downloads'].Fields

      const temp = []
      Object.entries(downloadsFields).map(([key, val]) => {
        if (val.Enabled) {
          temp.push({
            field: key,
            flex: 0.75,
            headerName: val.DisplayName,
            disableColumnMenu: true,
            renderCell: (params) =>
              key === 'Date' ? (
                new Date(params.row.CreatedDate || '').toLocaleString()
              ) : key === 'DownloadZip' ? (params.row.IsPurged) ? 'Not Available' : (
                <ContextButton
                  onClick={() => {
                    setDataRequestFilename(
                      `data-request-${format(
                        new Date(params.row.CreatedDate || ''),
                        'MM-dd-yyyy'
                      )}.zip`
                    )
                    GET_REPORT_BY_REPORT_OBJECT(
                      {
                        AZURE_STORAGE_ACCOUNT_ID: params.row.AzureStorageAccountConfigId,
                        REPORT_NAME: params.row.AzureImagePath,
                        SEARCH_VALUE: params.row.AzureImagePath.split('.zip')[0],
                        REQUEST_TYPE: 'FromUrl',
                        RETURN_TYPE: 'URL',
                        SOURCE_ID: Number(dataReqFilingId),
                        SOURCE_TYPE_ID: 869,
                        APP_REPORT_ID: 0,
                        IMAGE_DATA_TYPE_ID: 0,
                        FORM_DEFINITION_ID: 0,
                        LAST_MODIFIED_DATE: new Date(),
                      },
                      (res) => {
                        if (res.ok) {
                          res.text().then((url) => {
                            setDownloadURL(url)
                          })
                        } else {
                          dispatch(setErrorToast('Failed to download data request.'))
                        }
                      },
                      (err) => console.error(err)
                    )
                  }}
                >
                  Download
                </ContextButton>
              ) : (
                params.value
              ),
          })
        }
        return []
      })
      return temp
    } else return []
  }, [recordDetailSettings])

  useEffect(() => {
    if (downloadURL) {
      setDownloadOnNextTick(true)
    }
  }, [downloadURL])

  useEffect(() => {
    if (downloadOnNextTick) {
      downloader.current.download()
      setDownloadOnNextTick(false)
    }
  }, [downloadOnNextTick])

  const dataRequestItems = useMemo(() => {
    return (recordDetails.Downloads || []).map((item, i) => {
      return {
        ...item,
      }
    })
  }, [recordDetails.Downloads])

  const AddField = (fieldName, value, array) => {
    const setting = recordDetailSettings.FieldSettings || {}
    if (setting[fieldName] && setting[fieldName].Enabled === true) {
      array.push({ label: setting[fieldName].DisplayName, value: value })
    }
  }

  const generalDetails = useMemo(() => {
    const list = []
    AddField('Name', recordDetails.Name, list)
    if (recordDetails.NameOverride) AddField('NameOverride', recordDetails.NameOverride, list)
    AddField('Status', recordDetails.Status, list)
    AddField('RequestType', recordDetails.RequestType, list)
    AddField('FrequencyType', recordDetails.FrequencyType, list)
    // This IS SO JANKY
    if (recordDetailSettings.FieldSettings && recordDetailSettings.FieldSettings['Downloads']) {
      const receiptNumSetting = recordDetailSettings.FieldSettings['Downloads'].Fields['ReceiptNum']
      if (receiptNumSetting && receiptNumSetting.Enabled === true) {
        list.push({
          label: receiptNumSetting.DisplayName,
          value: appUserRights.includes('ReceiptView') ? (
            <div
              className="dlnTableLink"
              tabIndex={0}
              onClick={() => {
                history.push(`/accounting/receipt/${recordDetails.ReceiptNum}`)
              }}
            >
              {recordDetails.ReceiptNum}
            </div>
          ) : (
            recordDetails.ReceiptNum
          ),
        })
      }
    }
    // END JANKY
    AddField('User', recordDetails.User, list)
    AddField('CreatedDate', recordDetails.CreatedDate, list)
    AddField('ExpirationDate', recordDetails.ExpirationDate, list)

    return list
  })

  return (
    <div>
      <div className="row header-row">
        <h1>Data Request Record Details</h1>
      </div>
      <br />
      <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
        <div style={{ minWidth: '450px' }}>
          <DetailCard className="detailsCard" label="General Details">
            <DetailList details={generalDetails} topBorder="true" bottomBorder="true" />
          </DetailCard>
        </div>
        <GraniteDataGrid
          getRowId={(r) => r.DataRequestEntryId}
          columns={dataRequestColumns}
          rows={dataRequestItems}
          pageSize={5}
          rowsPerPageOptions={[5]}
        />
      </div>
      <br />
      <br />
      <br />
      <NotesPanel saveNote={saveNewNote} editNote={editNote} deleteNote={deleteNote} />
      <div className={`context-bar-wrapper `}>
        <ContextBar className="granite-context-bar">
          <div className="context-button-wrapper">
            <ContextButton
              leftIcon={
                <div className="checkmark-wrapper left">
                  <LeftArrowIcon />
                </div>
              }
              onClick={() => history.goBack()}
              variant="secondary"
            >
              Back
            </ContextButton>
          </div>
        </ContextBar>
      </div>
      <FileDownloader ref={downloader} href={downloadURL} fileName={dataRequestFilename} />
    </div>
  )
}

export default DataRequestRecordDetails
